import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { useQuery } from 'react-query';
import {
  fe41EaV5UpdatedCookingSuppliesEligibility,
  fe65EaEligibleHomeRemediation,
} from 'common/utils/FeatureFlags/flags';
import { screeningsApiClient } from 'src/api/config';
import { useFind } from 'src/api/APIHooks';
import {
  useScreening,
} from 'src/pages/facesheet/_id/eligibility/hooks/eligibilityApi';
import {
  useElAssessment,
} from 'src/pages/facesheet/_id/eligibility/hooks/useEligibilityAssessments';
import {
  ELIGIBILITY_ASSESSMENT_STATUS,
  getEligibleServicesUtil,
} from 'src/pages/facesheet/_id/eligibility/utils';

const FIELD_LABEL_CLASS = 'mb-0.5 text-sm leading-5 font-bold text-medium-grey';
const SECTION_TITLE_CLASS = 'text-base font-semibold leading-6 text-brand-blue';
const INITIATIVE_CONDITIONS_PAGE = { number: 1, size: 100 };
const NAVIGATION_ONLY_SERVICE = 'Navigation Services (Level 1)';
const ASSESSMENT_NEEDED_TITLE = 'Eligibility Assessment Needed';
const ELIGIBLE_SERVICES_TITLE = 'Client May Be Eligible';
const ELIGIBLE_SERVICES_MESSAGE = [
  'The client may be eligible for the following enhanced health-related',
  'social need (HRSN) programs.',
].join(' ');
const DRAFT_MESSAGE = [
  'Complete an eligibility assessment to determine the client\'s eligibility',
  'for enhanced health-related social need services.',
].join(' ');
const NOT_ELIGIBLE_TITLE = 'Client Not Eligible';
const NOT_ELIGIBLE_MESSAGE = [
  'Based on the assessment the client is not eligible for enhanced HRSN',
  'services. You can refer the client to non-enhanced services for any',
  'identified needs.',
].join(' ');
const LATEST_COMPLETED_ASSESSMENT_QUERY_KEY = 'social-care-plan-latest-completed-assessment';
const ESMF_TYPES = {
  enhancedPopulation: 'enhanced_population',
  clinicalCondition: 'clinical_condition',
  serviceDuplication: 'service_duplication',
};

function getConditionValues(conditions, esmfType) {
  const seen = new Set();

  return conditions.reduce((values, condition) => {
    const type = condition?.esmf_type;
    const description = condition?.initiative_description;
    const isActive = condition?.is_active === true;

    if (!isActive || type !== esmfType || !description || seen.has(description)) {
      return values;
    }

    seen.add(description);
    values.push(description);

    return values;
  }, []);
}

function useInitiativeConditions(contactId) {
  const { data: response, isLoading } = useFind(
    `people/${contactId}/initiative_conditions`,
    { is_active: true },
    {
      page: INITIATIVE_CONDITIONS_PAGE,
      queryConfig: {
        enabled: Boolean(contactId),
        placeholderData: undefined,
      },
    },
  ) || {};

  const responseConditions = response?.data?.data ?? [];

  return {
    enhancedPopulations: getConditionValues(responseConditions, ESMF_TYPES.enhancedPopulation),
    clinicalConditions: getConditionValues(responseConditions, ESMF_TYPES.clinicalCondition),
    serviceDuplications: getConditionValues(responseConditions, ESMF_TYPES.serviceDuplication),
    isLoading,
  };
}

export async function fetchLatestCompletedAssessment(contactId) {
  const response = await screeningsApiClient.get('/screenings', {
    params: {
      person_id: contactId,
      type: 'assessment',
      status: ELIGIBILITY_ASSESSMENT_STATUS.COMPLETE,
      sort_by: 'status_at',
      sort_direction: 'desc',
      limit: 1,
      offset: 0,
    },
  });

  return get(response, 'data.screens[0]', null);
}

function useLatestCompletedAssessment(contactId) {
  return useQuery(
    [LATEST_COMPLETED_ASSESSMENT_QUERY_KEY, contactId],
    () => fetchLatestCompletedAssessment(contactId),
    {
      enabled: Boolean(contactId),
      staleTime: 10 * 60 * 1000,
      refetchInterval: false,
      retry: false,
    },
  );
}

function getEligibleProgramsContent(hasCompletedAssessment, eligibleServices) {
  if (!hasCompletedAssessment) {
    return {
      title: ASSESSMENT_NEEDED_TITLE,
      message: DRAFT_MESSAGE,
      services: [],
    };
  }

  if (eligibleServices.length > 0) {
    const isNavigationOnly =
      eligibleServices.length === 1 &&
      eligibleServices[0] === NAVIGATION_ONLY_SERVICE;

    return {
      title: isNavigationOnly ? NOT_ELIGIBLE_TITLE : ELIGIBLE_SERVICES_TITLE,
      message: isNavigationOnly ? NOT_ELIGIBLE_MESSAGE : ELIGIBLE_SERVICES_MESSAGE,
      services: eligibleServices,
    };
  }

  return {
    title: NOT_ELIGIBLE_TITLE,
    message: NOT_ELIGIBLE_MESSAGE,
    services: [],
  };
}

function Field({ label, value, className }) {
  return (
    <div className={`min-w-0 border-b border-solid border-light-border-blue py-4 ${className}`}>
      <div className="mb-1">
        <div className={FIELD_LABEL_CLASS}>
          {label}
        </div>
      </div>
      <div className="font-regular-font text-sm leading-5 text-brand-blue whitespace-pre-wrap break-words">
        {value}
      </div>
    </div>
  );
}

Field.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

Field.defaultProps = {
  className: '',
};

export function HRSNServiceConsiderationsSection({
  className,
  contact,
  updatedCookingSuppliesEligibilityEnabled,
  eligibleHomeRemediationEnabled,
}) {
  const {
    enhancedPopulations,
    clinicalConditions,
    serviceDuplications,
    isLoading,
  } = useInitiativeConditions(contact?.id);
  const {
    data: latestCompletedAssessmentSummary,
    isLoading: isLoadingLatestCompletedAssessment,
  } = useLatestCompletedAssessment(contact?.id);
  const latestCompletedAssessmentId = latestCompletedAssessmentSummary?.id;
  const {
    data: assessment,
    isFetching: isFetchingAssessment,
  } = useElAssessment(
    latestCompletedAssessmentId,
    {
      enabled: Boolean(latestCompletedAssessmentId),
    },
  );
  const relatedScreeningId = get(assessment, 'data.screen.related_screen_id');
  const {
    data: relatedScreening,
    isFetching: isFetchingRelatedScreening,
  } = useScreening(
    relatedScreeningId,
    {
      enabled: Boolean(relatedScreeningId),
    },
  );

  const enhancedPopulationsValue = isLoading ? '--' : (enhancedPopulations.join(', ') || '--');
  const clinicalConditionsValue = isLoading ? '--' : (clinicalConditions.join(', ') || '--');
  const serviceDuplicationsValue = isLoading ? '--' : (serviceDuplications.join(', ') || '--');
  const identifiedNeedsObject = get(relatedScreening, 'identified_needs', {});
  const identifiedNeeds = identifiedNeedsObject ? Object.keys(identifiedNeedsObject) : [];
  const eligibleServices =
    relatedScreening && assessment ? (getEligibleServicesUtil(assessment, identifiedNeeds, {
      updatedCookingSuppliesEligibilityEnabled,
      eligibleHomeRemediationEnabled,
    }) || []) : [];
  const eligiblePrograms = getEligibleProgramsContent(
    Boolean(latestCompletedAssessmentSummary),
    eligibleServices,
  );
  const isLoadingEligiblePrograms =
    isLoadingLatestCompletedAssessment ||
    isFetchingAssessment ||
    (Boolean(relatedScreeningId) && isFetchingRelatedScreening);

  return (
    <div className={`px-5 py-5 ${className}`}>
      <div className="grid grid-cols-2 gap-x-10">
        <div className="flex flex-col">
          <Field
            label="Enhanced Populations"
            value={enhancedPopulationsValue}
          />
          <Field
            className="border-b-0"
            label="Service Duplication"
            value={serviceDuplicationsValue}
          />
        </div>
        <div className="flex flex-col">
          <Field
            className="border-b-0"
            label="Clinical Conditions"
            value={clinicalConditionsValue}
          />
        </div>
      </div>

      <div className="pt-6">
        <div className="mb-5 font-medium-font text-xs leading-4 text-medium-grey uppercase tracking-wide">
          NY 1115 Enhanced Services
        </div>
        <div className="rounded border border-solid border-light-border-blue p-4">
          <div className={`mb-2 ${SECTION_TITLE_CLASS}`}>
            {eligiblePrograms.title}
          </div>
          <div className="mb-4 font-regular-font text-base leading-6 text-brand-blue">
            {isLoadingEligiblePrograms ? '--' : eligiblePrograms.message}
          </div>
          {!isLoadingEligiblePrograms && eligiblePrograms.services.length > 0 && (
            <ul className="m-0 list-disc pl-5 font-regular-font text-base leading-6 text-brand-blue">
              {eligiblePrograms.services.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

HRSNServiceConsiderationsSection.propTypes = {
  className: PropTypes.string,
  contact: PropTypes.shape({
    id: PropTypes.string,
  }),
  updatedCookingSuppliesEligibilityEnabled: PropTypes.bool,
  eligibleHomeRemediationEnabled: PropTypes.bool,
};

HRSNServiceConsiderationsSection.defaultProps = {
  className: '',
  contact: null,
  updatedCookingSuppliesEligibilityEnabled: false,
  eligibleHomeRemediationEnabled: false,
};

function mapStateToProps(state) {
  return {
    updatedCookingSuppliesEligibilityEnabled: fe41EaV5UpdatedCookingSuppliesEligibility(state),
    eligibleHomeRemediationEnabled: fe65EaEligibleHomeRemediation(state),
  };
}

export default connect(mapStateToProps)(HRSNServiceConsiderationsSection);
